/* eslint-disable */
import { ChangeEvent, useEffect, useState } from 'react';
import {
  Box,
  useTheme,
  Typography,
  Button,
  DialogContent,
  Dialog,
  DialogActions,
  LinearProgress,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  type GridColumns,
  type GridValidRowModel,
  GridSortModel,
} from '@mui/x-data-grid';
import SendIcon from '@mui/icons-material/Send';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import EmployeeUploadFormat from '../../../assets/formatfiles/AddEmployeeAdminFormat.xlsm';
import { DialogBox } from 'components/shared';
import CreateEmployee from './CreateEmployee';
import { colorTheme } from 'theme';
import Buttons from 'components/elements/Buttons';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  getAllOrganizationEmployeeWithRoleName,
  removeOrganizationEmployee,
  resendInviteLinkToOrgEmployee,
  uploadFileDataCreation,
  uploadFileDataEmailCreation,
  uploadFileDataValidation,
} from 'service/UserService';
import { RootState } from 'store';
import { AlertModal, CustomDataGrid } from 'components';
import { useNavigate, useLocation } from 'react-router-dom';
import { setSnackBarFailed, setSnackBarSuccess } from 'store/snackBar';
import { strings } from 'i18n/Strings';
import { useDispatch, useSelector } from 'react-redux';
import {
  setCurrentPage,
  setSortBy,
  setSortOrder,
} from 'store/component/pagination';
import { checkSortingValues } from 'utils/GeneralUtil';
import ConfirmationModal from 'components/confirmation-modal';
import * as XLSX from 'xlsx';
import ErrorDialog from '../../employerDetails/employeeGrid/uploadError';

interface IEmployeeProps {
  empId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  employerContribution: string;
  employeeContribution: string;
  emailSubscription: boolean;
}

const Employees = (): JSX.Element => {
  const theme = useTheme();
  const colors = colorTheme(theme.palette.mode);
  const dispatch = useDispatch();
  const [openAddEmployee, setOpenAddEmployee] = useState(false);
  const [openEmployeeForm, setOpenEmployeeForm] = useState(false);
  const [openUploadOptions, setOpenUploadOptions] = useState(false);
  // const [openResentInviteDialog, setOpenResetInviteDialog] = useState(false);  -- not needed now
  const [employeeData, setEmployeeData] = useState<IEmployeeProps[]>([]);
  const [employeeDataFile, setEmployeeDataFile] = useState<File | null>();
  const [updateEmployeeId, setUpdateEmployeeId] = useState('');
  const [openFileUploadDialog, setOpenFileUploadDialog] = useState(false);
  const [loader, setLoader] = useState(false);
  const [errorMessage, setErrorMessages] = useState<String[]>([]);
  const [openErrorMessageDialog, setOpenErrorMessageDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [toBeDeletedEmployee, setToBeDeletedEmployee] = useState('');
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [comments, setComments] = useState('');
  const [openCommentDialog, setOpenCommentDialog] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [pageCount, setPageCount] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  type GridRowData = Record<string, unknown>;
  const pageInfo = useSelector((state) => state?.pagination?.employees);

  const { orgId, empId } = useSelector((state: RootState) => state.onboarding);
  const { userDetails } = useSelector((state: RootState) => state.auth);
  const [showError, setShowError] = useState(false);
  const [errorData, setErrorData] = useState([]);
  const [uploadFileStaus, setUploadFileStaus] = useState('');

  const navigate = useNavigate();
  const location = useLocation();

  const orgEmpId = userDetails?.organizationEmployee?.orgEmpId || '';

  const columns: GridColumns<GridValidRowModel> = [
    {
      field: 'firstName',
      flex: 1,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.firstName}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.firstName}
        </Typography>
      ),
    },
    {
      field: 'lastName',
      flex: 0.8,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.lastName}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.lastName}
        </Typography>
      ),
    },
    {
      field: 'email',
      flex: 1.5,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.emailAddress}
        </Typography>
      ),
      renderCell: ({ row }) => {
        const maxLength = 23; // Adjust as needed
        const email = row.email;
        const truncatedEmail =
          email.length > maxLength ? email.slice(0, maxLength) + '...' : email;

        return (
          <Tooltip title={email} placement="top">
            <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
              {truncatedEmail}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'status',
      flex: 1,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.status}
        </Typography>
      ),
      renderCell: ({ row }) => {
        return (
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {row.status}
          </Typography>
        );
      },
    },
    {
      field: 'resendInvite',
      headerName: 'Resend Invite',
      headerAlign: 'center',
      sortable: false,
      flex: 1,
      renderCell: ({ row }) => (
        <Box m="0 auto" display="flex" justifyContent="space-evenly">
          {row.status === 'Not Registered' && row.emailSubscription === true ? (
            <Button
              variant="outlined"
              color="success"
              disabled={false}
              onClick={(e) => {
                e.stopPropagation();
                void handleResentInvite(row.orgEmpId);
              }}
              endIcon={<SendIcon />}
            >
              {strings.displayText.invite}
            </Button>
          ) : (
            <Button
              variant="outlined"
              color="success"
              disabled={true}
              endIcon={<SendIcon />}
            >
              {strings.displayText.invite}
            </Button>
          )}
        </Box>
      ),
    },
  ];

  const updateEmployeeData = (): void => {
    if (orgId) {
      getAllOrganizationEmployeeWithRoleName({
        id: orgId.toString(),
        page: Number(pageInfo?.page) + 1,
        size: pageInfo?.pageSize,
        sortBy: pageInfo?.sortBy,
        sortOrder: pageInfo?.sortOrder,
        searchValue,
      })
        .then((res) => {
          if (typeof res !== 'undefined') {
            const { employees, count } = res as {
              employees: IEmployeeProps[];
              count: number;
            };
            if (employees) {
              setEmployeeData(employees);
              setPageCount(count);
            }
          }
        })
        .catch((err: Error) => {
          throw err;
        });
    }
  };

  useEffect(() => {
    updateEmployeeData();
  }, [
    pageInfo?.page,
    pageInfo.pageSize,
    pageInfo?.sortBy,
    pageInfo?.sortOrder,
    searchValue,
  ]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const openAddEmployeeParam = searchParams.get('openAddEmployee');
    if (openAddEmployeeParam === 'true') {
      setOpenAddEmployee(true);
      setUpdateEmployeeId('');
      // Clean up the URL by removing the query parameter
      navigate('/employees', { replace: true });
    }
  }, [location.search, navigate]);

  const AddEmployeeChoices = (): JSX.Element => {
    return (
      <Box
        display={'flex'}
        flexDirection={'column'}
        justifyContent={'space-between'}
      >
        <Buttons
          sx={{ m: 2, bgcolor: colors.greenAccent[500] }}
          onClick={() => {
            setOpenEmployeeForm(true);
            setOpenAddEmployee(false);
          }}
        >
          Create Employee
        </Buttons>
        <Buttons
          sx={{ m: 2, bgcolor: colors.greenAccent[500] }}
          onClick={() => {
            setOpenUploadOptions(true);
            setOpenAddEmployee(false);
          }}
        >
          Upload File
        </Buttons>
      </Box>
    );
  };

  const UploadChoices = (): JSX.Element => {
    return (
      <Box
        display={'flex'}
        flexDirection={'column'}
        justifyContent={'space-between'}
      >
        <Button
          sx={{
            m: 1,
            bgcolor: colors.greenAccent[500],
            color: '#fff',
            '&:hover': {
              bgcolor: colors.greenAccent[500],
            },
          }}
          startIcon={<DownloadIcon />}
        >
          <a
            style={{ textDecoration: 'none', color: '#fff' }}
            href={EmployeeUploadFormat}
            target="_blank"
            download
            rel="noreferrer"
          >
            Download format
          </a>
        </Button>
        <Button
          sx={{
            m: 2,
            bgcolor: colors.greenAccent[500],
            color: '#fff',
            '&:hover': {
              bgcolor: colors.greenAccent[500],
            },
          }}
          startIcon={<UploadIcon />}
          component="label"
        >
          Upload Data
          <input
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              if (e.target.files) {
                setEmployeeDataFile(e.target.files[0]);
                uploadEmployeeDataFile(e.target.files[0], orgId);
                setOpenUploadOptions(false);
                setOpenFileUploadDialog(true);
              }
            }}
            hidden
            accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
            type="file"
          />
        </Button>
      </Box>
    );
  };

  const handleResentInvite = async (orgEmpId: number): Promise<void> => {
    try {
      await resendInviteLinkToOrgEmployee(orgEmpId);
      dispatch(
        setSnackBarSuccess({
          snackBarMessage: strings.displayText.intiveSentSuccess,
        }),
      );
    } catch (error) {
      console.log(error);
    }
  };

  const uploadEmployeeDataFile = async (
    file: File,
    orgId: number,
  ): Promise<void> => {
    if (file) {
      setLoader(true);
      setUploadFileStaus('Reading file contents...!');
      if (!file) return;

      const reader = new FileReader();

      reader.onload = async (evt: unknown) => {
        const data = evt.target.result;
        const workbook = XLSX.read(data, { type: 'binary' });

        const sheetName = workbook.SheetNames[0]; // read first sheet
        const sheet = workbook.Sheets[sheetName];

        const json = XLSX.utils.sheet_to_json(sheet, { defval: '' }); // defval prevents `undefined`

        setUploadFileStaus('Running validation checks...!');
        await uploadFileDataValidation(json, orgId, empId)
          .then(async () => {
            setUploadFileStaus('Data verified. Creating new user records...');
            await uploadFileDataCreation(json, orgId, empId)
              .then(async () => {
                setUploadFileStaus(
                  'User accounts created. Dispatching email notifications...',
                );
                await uploadFileDataEmailCreation(
                  json.map((a: unknown) => ({ email: a['Email*'] })),
                  orgId,
                  empId,
                );
                setLoader(false);
                setSuccessMessage(
                  strings.displayText.employeeAddSuccessMessage,
                );
                updateEmployeeData();
              })
              .catch((err) => {
                console.log(err);
                setLoader(false);
                setOpenFileUploadDialog(false);
              });
            setSuccessMessage(strings.displayText.employeeAddSuccessMessage);
          })
          .catch((error) => {
            if (error?.response?.data?.data?.length > 0) {
              setShowError(true);
              setErrorData(error.response.data.data);
            }
            setErrorMessages(error?.response?.data?.message.split(','));
            setOpenErrorMessageDialog(true);
            setLoader(false);
            setOpenFileUploadDialog(false);
          });
      };

      reader.readAsBinaryString(file);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (toBeDeletedEmployee?.orgEmpId !== undefined) {
      const res = await removeOrganizationEmployee(
        Number(toBeDeletedEmployee.orgEmpId),
        comments,
      ); // convert to number
      if (res) {
        dispatch(
          setSnackBarSuccess({
            snackBarMessage: strings.displayText.employeeDeleteSuccessfully,
          }),
        );
      } else {
        dispatch(
          setSnackBarFailed({
            snackBarMessage: strings.displayText.employeeDeleteFailed,
          }),
        );
      }

      res && updateEmployeeData();
      setOpenDeleteDialog(false);
    }
  };
  const handleSearch = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    setSearchValue(event.target.value);
    dispatch(setCurrentPage({ key: 'employees', value: 0 }));
  };

  const handleSortChange = (model: GridSortModel): void => {
    dispatch(
      setSortBy({
        key: 'employees',
        value: model[0].field,
      }),
    );
    dispatch(setSortOrder({ key: 'employees', value: model[0].sort }));
    dispatch(setCurrentPage({ key: 'employees', value: 0 }));
  };

  const handleCreateDialogClose = () => {
    if (updateEmployeeId) {
      setOpenEmployeeForm(false);
    } else {
      setConfirmationModal(true);
    }
  };

  const handleCloseEmployeeForm = (): void => {
    setOpenEmployeeForm(false);
  };

  const handleConfirmationModal = (): void => {
    setConfirmationModal(false);
    setOpenEmployeeForm(false);
  };

  const handleConfirmationModalContinue = (): void => {
    setConfirmationModal(false);
  };

  return (
    <Box>
      <CustomDataGrid
        title="Employees"
        columns={columns}
        rows={employeeData}
        searchEnabled={true}
        handleSearch={handleSearch}
        searchPlaceholder={strings.displayText.searchLabel1}
        addButtonTitle={strings.displayText.addEmpoyeeLabel}
        onAddButtonClick={() => {
          setOpenAddEmployee(true);
          setUpdateEmployeeId('');
          // ref.current?.focus();
        }}
        actionBtnText1="edit"
        ActionBtnIcon1={() => <EditIcon />}
        onActionBtnClick1={(row, event) => {
          if (row.status === 'Leaver') return;
          event.stopPropagation();
          setOpenEmployeeForm(true);
          setUpdateEmployeeId(row.orgEmpId);
        }}
        actionBtnText2="delete"
        ActionBtnIcon2={() => <DeleteIcon />}
        onActionBtnClick2={(row, event) => {
          if (row.status === 'Leaver') return;

          if (orgEmpId === row.orgEmpId) {
            event.stopPropagation();
            dispatch(
              setSnackBarFailed({
                snackBarMessage: strings.displayText.sameUserCantDelete,
              }),
            );
            return;
          }

          event.stopPropagation();
          setOpenDeleteDialog(true);
          setToBeDeletedEmployee(row);
        }}
        getRowId={(row: GridRowData) =>
          `${String(row.empId)}${row.email ? row.email : ''}`
        }
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
        }
        onRowClick={(row: GridRowData, event) => {
          if (row.row.status === 'Leaver') {
            setOpenCommentDialog(true);
            setComments(row.row.comments);
          } else {
            event.preventDefault();
            navigate(`/employee-details/${row.row.orgEmpId}`, {
              state: { employeeData: row.row },
            });
          }
        }}
        currentPage="employees"
        pageCount={pageCount}
        sortModel={checkSortingValues(pageInfo)}
        sortingMode={strings.displayText.sortingServer}
        onSortModelChange={(mode) => {
          handleSortChange(mode);
        }}
      />

      <DialogBox
        PaperProps={{ sx: { width: '35%' } }}
        open={openAddEmployee}
        onClose={() => {
          setOpenAddEmployee(false);
        }}
        screenWidth={'lg'}
        title={strings.displayText.addEmployeeModelTitle}
      >
        <AddEmployeeChoices />
      </DialogBox>
      <DialogBox
        open={openEmployeeForm}
        onClose={handleCreateDialogClose}
        screenWidth={'md'}
        title={
          updateEmployeeId
            ? strings.displayText.editEmployee
            : strings.displayText.createEmployee
        }
      >
        <CreateEmployee
          updateEmployeeId={updateEmployeeId}
          updateEmployeeData={updateEmployeeData}
          onClose={handleCloseEmployeeForm}
          page={updateEmployeeId ? 'Edit Employee' : 'Create Employee'}
          hidePhoneField={true}
        />
      </DialogBox>
      <ConfirmationModal
        open={confirmationModal}
        onClose={handleConfirmationModal}
        onContinue={handleConfirmationModalContinue}
      />
      <DialogBox
        open={openUploadOptions}
        onClose={() => {
          setOpenUploadOptions(false);
        }}
        screenWidth={'md'}
        title={'Upload'}
      >
        <UploadChoices />
      </DialogBox>
      <Dialog
        disableEscapeKeyDown
        open={openFileUploadDialog}
        onClose={() => {
          setOpenFileUploadDialog(false);
        }}
      >
        {openFileUploadDialog && loader && (
          <Box>
            <Box sx={{ m: 3 }}>
              <Typography sx={{ mt: 1 }}>{employeeDataFile?.name}</Typography>
              <LinearProgress sx={{ m: 2 }} />
              <Typography sx={{ mt: 1 }}>{uploadFileStaus}</Typography>
            </Box>
          </Box>
        )}
        {openFileUploadDialog && !loader && successMessage && (
          <Box sx={{ m: 5 }} display={'flex'} flexDirection={'column'}>
            <Box sx={{ alignSelf: 'center' }}>
              <CheckCircleIcon
                fontSize={'large'}
                htmlColor={colors.greenAccent[500]}
              />
            </Box>
            <Typography sx={{ m: 1, fontWeight: '900', fontSize: '20' }}>
              Upload Successful!
            </Typography>

            <Button
              sx={{ color: colors.greenAccent[500] }}
              onClick={setOpenFileUploadDialog(false)}
            >
              Close
            </Button>
          </Box>
        )}
      </Dialog>
      {errorMessage.length > 0 && (
        <Dialog
          disableEscapeKeyDown
          open={openErrorMessageDialog}
          onClose={() => {
            setOpenErrorMessageDialog(false);
          }}
        >
          <DialogContent>
            <Box display={'flex'} flexDirection={'column'}>
              <Typography alignSelf={'center'} fotntWeight={'700'}>
                Upload Failed!
              </Typography>
              {errorMessage.map((message) => (
                <Typography sx={{ mt: 1, color: 'red' }}>
                  {' '}
                  - {message}
                </Typography>
              ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setEmployeeDataFile(null);
                setErrorMessages([]);
                setOpenUploadOptions(true);
                setOpenErrorMessageDialog(false);
              }}
              startIcon={<RefreshIcon />}
              variant="text"
              sx={{ color: colors.greenAccent[500], fontWeight: 'bold' }}
            >
              Try Again
            </Button>
          </DialogActions>
        </Dialog>
      )}
      <AlertModal
        isOrganizationEmployee={true}
        comments={comments}
        setComments={setComments}
        open={openDeleteDialog}
        onClose={() => {
          setOpenDeleteDialog(false);
        }}
        handleConfirm={handleDelete}
        title={`${strings.displayText.deleteConfirmationText} employee `}
        isButtonForConfirmation={true}
        email={String(toBeDeletedEmployee?.email)}
      />
      <Dialog
        open={openCommentDialog}
        onClose={() => {
          setOpenCommentDialog(false);
        }}
      >
        <DialogContent>
          <Typography variant="h4" component="h2" sx={{ textAlign: 'center' }}>
            The employee is Leaver.
          </Typography>
          {comments ? (
            <Typography
              mt={2}
              component="h1"
              variant="h5"
              style={{ fontSize: '16px' }}
            >
              {' '}
              Comments : {comments}
            </Typography>
          ) : (
            <Typography
              mt={2}
              component="h1"
              variant="h5"
              style={{ fontSize: '12px' }}
            >
              No comments are provided
            </Typography>
          )}
        </DialogContent>
      </Dialog>
      <ErrorDialog
        isModalOpen={showError}
        errData={errorData}
        handleCreateDialogClose={() => {
          setShowError(false);
          setErrorData([]);
        }}
      />
    </Box>
  );
};

export default Employees;
